/**
 * BasketballVideoPlayer - Video player with frame-by-frame controls
 * Features: Play/Pause, Step Forward/Back, Playback speed, Timeline scrubber
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Rewind,
  FastForward,
  Volume2,
  VolumeX,
  Maximize2,
  Settings,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

// Frame step amount in seconds (1 frame at 30fps = ~0.033s)
const FRAME_STEP = 0.033;
const SKIP_SECONDS = 5;

const BasketballVideoPlayer = ({
  videoUrl,
  markers = [],
  currentMarker,
  onTimeUpdate,
  onMarkerHit,
  onMarkerClick,
  posterUrl,
  isAnalyzing = false,
  translations = {},
}) => {
  const videoRef = useRef(null);
  const progressRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(true);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showControls, setShowControls] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const hideControlsTimeout = useRef(null);

  // Format time to MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle time update
  const handleTimeUpdate = useCallback(() => {
    const video = videoRef.current;
    if (!video || isDragging) return;

    const time = video.currentTime;
    setCurrentTime(time);
    onTimeUpdate?.(time);

    // Check if we hit a marker (within 0.5 second tolerance)
    const hitMarker = markers.find(
      (m) => Math.abs(m.timeSeconds - time) < 0.5 && m.timeSeconds <= time
    );
    if (hitMarker && hitMarker.id !== currentMarker?.id) {
      onMarkerHit?.(hitMarker);
    }
  }, [markers, currentMarker, onMarkerHit, onTimeUpdate, isDragging]);

  // Play/Pause toggle
  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      video.play();
      setIsPlaying(true);
    } else {
      video.pause();
      setIsPlaying(false);
    }
  }, []);

  // Step forward one frame
  const stepForward = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.pause();
    setIsPlaying(false);
    video.currentTime = Math.min(video.duration, video.currentTime + FRAME_STEP);
  }, []);

  // Step backward one frame
  const stepBackward = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.pause();
    setIsPlaying(false);
    video.currentTime = Math.max(0, video.currentTime - FRAME_STEP);
  }, []);

  // Skip forward/backward
  const skip = useCallback((seconds) => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = Math.max(0, Math.min(video.duration, video.currentTime + seconds));
  }, []);

  // Handle progress bar click
  const handleProgressClick = useCallback((e) => {
    const video = videoRef.current;
    const progress = progressRef.current;
    if (!video || !progress) return;

    const rect = progress.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    video.currentTime = pos * video.duration;
  }, []);

  // Handle progress bar drag
  const handleProgressDrag = useCallback((e) => {
    if (!isDragging) return;
    const video = videoRef.current;
    const progress = progressRef.current;
    if (!video || !progress) return;

    const rect = progress.getBoundingClientRect();
    const pos = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    video.currentTime = pos * video.duration;
    setCurrentTime(pos * video.duration);
  }, [isDragging]);

  // Toggle mute
  const toggleMute = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  }, []);

  // Set playback rate
  const setSpeed = useCallback((rate) => {
    const video = videoRef.current;
    if (!video) return;
    video.playbackRate = rate;
    setPlaybackRate(rate);
    setShowSpeedMenu(false);
  }, []);

  // Fullscreen toggle
  const toggleFullscreen = useCallback(() => {
    const container = videoRef.current?.parentElement?.parentElement;
    if (!container) return;

    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      container.requestFullscreen();
    }
  }, []);

  // Auto-hide controls
  const handleMouseMove = useCallback(() => {
    setShowControls(true);
    if (hideControlsTimeout.current) {
      clearTimeout(hideControlsTimeout.current);
    }
    hideControlsTimeout.current = setTimeout(() => {
      if (isPlaying) setShowControls(false);
    }, 3000);
  }, [isPlaying]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      switch (e.key) {
        case ' ':
        case 'k':
          e.preventDefault();
          togglePlay();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (e.shiftKey) stepBackward();
          else skip(-SKIP_SECONDS);
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (e.shiftKey) stepForward();
          else skip(SKIP_SECONDS);
          break;
        case ',':
          e.preventDefault();
          stepBackward();
          break;
        case '.':
          e.preventDefault();
          stepForward();
          break;
        case 'm':
          e.preventDefault();
          toggleMute();
          break;
        case 'f':
          e.preventDefault();
          toggleFullscreen();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [togglePlay, stepBackward, stepForward, skip, toggleMute, toggleFullscreen]);

  // Mouse up handler for drag
  useEffect(() => {
    const handleMouseUp = () => setIsDragging(false);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('mousemove', handleProgressDrag);
    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('mousemove', handleProgressDrag);
    };
  }, [handleProgressDrag]);

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div
      className="relative bg-black rounded-xl overflow-hidden group"
      onMouseMove={handleMouseMove}
      onMouseLeave={() => isPlaying && setShowControls(false)}
    >
      {/* Video element */}
      <video
        ref={videoRef}
        src={videoUrl}
        poster={posterUrl}
        className="w-full aspect-video object-contain bg-black"
        onLoadedMetadata={(e) => setDuration(e.target.duration)}
        onTimeUpdate={handleTimeUpdate}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
        muted={isMuted}
        playsInline
      />

      {/* Analyzing overlay */}
      {isAnalyzing && (
        <div className="absolute inset-0 bg-black/60 flex items-center justify-center z-20">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-yellow-400 font-mono text-sm uppercase tracking-wider">
              {translations.analyzing || 'Analyzing...'}
            </p>
          </div>
        </div>
      )}

      {/* Controls overlay */}
      <div
        className={`absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/40 transition-opacity duration-300 ${
          showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Top bar - Title */}
        <div className="absolute top-0 left-0 right-0 p-4">
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 bg-yellow-400 text-black text-xs font-bold rounded">
              THE WIRE
            </span>
            <span className="text-white text-sm font-medium">
              Basketball Intelligence Report
            </span>
          </div>
        </div>

        {/* Center play button */}
        <button
          onClick={togglePlay}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 rounded-full bg-white/20 backdrop-blur flex items-center justify-center hover:bg-white/30 transition-colors"
        >
          {isPlaying ? (
            <Pause className="w-10 h-10 text-white" />
          ) : (
            <Play className="w-10 h-10 text-white ml-1" />
          )}
        </button>

        {/* Bottom controls */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          {/* Synergy-style Timeline with Segments */}
          <div className="mb-3">
            {/* Segment Labels Row */}
            <div className="relative h-6 mb-1">
              {markers.map((marker, idx) => {
                const position = duration > 0 ? (marker.timeSeconds / duration) * 100 : 0;
                const nextMarker = markers[idx + 1];
                const endPosition = nextMarker
                  ? (nextMarker.timeSeconds / duration) * 100
                  : position + 3; // 3% width if last marker
                const width = Math.min(endPosition - position, 15); // Max 15% width
                const isActive = currentMarker?.id === marker.id;

                return (
                  <button
                    key={`label-${marker.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onMarkerClick?.(marker);
                      if (videoRef.current) {
                        videoRef.current.currentTime = marker.timeSeconds;
                      }
                    }}
                    className={`absolute text-[10px] font-medium truncate px-1 py-0.5 rounded transition-all cursor-pointer ${
                      isActive
                        ? 'bg-yellow-400 text-black z-20 scale-105'
                        : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white z-10'
                    }`}
                    style={{
                      left: `${position}%`,
                      maxWidth: `${width}%`,
                      minWidth: '40px',
                    }}
                    title={`${marker.event} @ ${marker.timeVideo}`}
                  >
                    {marker.timeVideo}
                  </button>
                );
              })}
            </div>

            {/* Main Timeline with Colored Segments */}
            <div
              ref={progressRef}
              className="relative h-3 bg-white/10 rounded cursor-pointer group/progress overflow-hidden"
              onClick={handleProgressClick}
              onMouseDown={() => setIsDragging(true)}
            >
              {/* Colored Segment Sections (Synergy-style) */}
              {markers.map((marker, idx) => {
                const position = duration > 0 ? (marker.timeSeconds / duration) * 100 : 0;
                const nextMarker = markers[idx + 1];
                const endPosition = nextMarker
                  ? (nextMarker.timeSeconds / duration) * 100
                  : Math.min(position + 5, 100); // 5% if last
                const width = endPosition - position;
                const isActive = currentMarker?.id === marker.id;
                const isPast = duration > 0 && (marker.timeSeconds / duration) * 100 < progressPercent;

                return (
                  <div
                    key={`segment-${marker.id}`}
                    className={`absolute top-0 h-full transition-opacity ${
                      isActive ? 'opacity-100 z-10' : isPast ? 'opacity-60' : 'opacity-40'
                    }`}
                    style={{
                      left: `${position}%`,
                      width: `${width}%`,
                      backgroundColor: getCategoryColor(marker.category),
                    }}
                  />
                );
              })}

              {/* Progress fill overlay */}
              <div
                className="absolute left-0 top-0 h-full bg-white/30 z-20 pointer-events-none"
                style={{ width: `${progressPercent}%` }}
              />

              {/* Playhead */}
              <div
                className="absolute top-1/2 -translate-y-1/2 w-1 h-5 bg-yellow-400 z-30 shadow-lg"
                style={{ left: `${progressPercent}%`, transform: 'translateX(-50%) translateY(-50%)' }}
              />

              {/* Hover expansion playhead */}
              <div
                className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full shadow-lg opacity-0 group-hover/progress:opacity-100 transition-opacity z-40"
                style={{ left: `${progressPercent}%`, transform: 'translateX(-50%) translateY(-50%)' }}
              />
            </div>

            {/* Category Legend */}
            <div className="flex flex-wrap gap-2 mt-2 justify-center">
              {[
                { key: 'clutch', label: 'Clutch' },
                { key: 'shooting', label: 'Shooting' },
                { key: 'drive', label: 'Drive' },
                { key: 'playmaking', label: 'Playmaking' },
                { key: 'defense', label: 'Defense' },
                { key: 'biomechanics', label: 'Biomech' },
                { key: 'fatigue', label: 'Fatigue' },
              ].map(({ key, label }) => (
                <div key={key} className="flex items-center gap-1">
                  <div
                    className="w-2 h-2 rounded-sm"
                    style={{ backgroundColor: getCategoryColor(key) }}
                  />
                  <span className="text-[10px] text-white/50">{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Control buttons */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {/* Play/Pause */}
              <button
                onClick={togglePlay}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                {isPlaying ? (
                  <Pause className="w-5 h-5 text-white" />
                ) : (
                  <Play className="w-5 h-5 text-white" />
                )}
              </button>

              {/* Skip back */}
              <button
                onClick={() => skip(-SKIP_SECONDS)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                title="Skip back 5s"
              >
                <Rewind className="w-5 h-5 text-white" />
              </button>

              {/* Frame back */}
              <button
                onClick={stepBackward}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                title="Previous frame (,)"
              >
                <ChevronLeft className="w-5 h-5 text-white" />
              </button>

              {/* Frame forward */}
              <button
                onClick={stepForward}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                title="Next frame (.)"
              >
                <ChevronRight className="w-5 h-5 text-white" />
              </button>

              {/* Skip forward */}
              <button
                onClick={() => skip(SKIP_SECONDS)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                title="Skip forward 5s"
              >
                <FastForward className="w-5 h-5 text-white" />
              </button>

              {/* Time display */}
              <span className="text-white text-sm font-mono ml-2">
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>
            </div>

            <div className="flex items-center gap-2">
              {/* Volume */}
              <button
                onClick={toggleMute}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                {isMuted ? (
                  <VolumeX className="w-5 h-5 text-white" />
                ) : (
                  <Volume2 className="w-5 h-5 text-white" />
                )}
              </button>

              {/* Playback speed */}
              <div className="relative">
                <button
                  onClick={() => setShowSpeedMenu(!showSpeedMenu)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors flex items-center gap-1"
                >
                  <Settings className="w-5 h-5 text-white" />
                  <span className="text-white text-xs">{playbackRate}x</span>
                </button>

                {showSpeedMenu && (
                  <div className="absolute bottom-full right-0 mb-2 bg-black/95 backdrop-blur-xl rounded-lg border border-white/[0.12] p-1 min-w-[80px]">
                    {[0.25, 0.5, 0.75, 1, 1.25, 1.5, 2].map((rate) => (
                      <button
                        key={rate}
                        onClick={() => setSpeed(rate)}
                        className={`w-full px-3 py-1.5 text-left text-sm rounded hover:bg-white/[0.08] transition-colors ${
                          playbackRate === rate ? 'text-yellow-400' : 'text-white'
                        }`}
                      >
                        {rate}x
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Fullscreen */}
              <button
                onClick={toggleFullscreen}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <Maximize2 className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Keyboard shortcuts hint */}
      <div className="absolute bottom-20 left-4 text-white/40 text-xs font-mono hidden group-hover:block">
        Space: Play/Pause | ← →: Skip 5s | , .: Frame step | F: Fullscreen
      </div>
    </div>
  );
};

// Helper function to get category colors
function getCategoryColor(category) {
  const colors = {
    clutch: '#FFD700',      // Gold
    shooting: '#10B981',    // Green
    drive: '#3B82F6',       // Blue
    playmaking: '#8B5CF6',  // Purple
    defense: '#EF4444',     // Red
    rebounding: '#F97316',  // Orange
    post: '#EC4899',        // Pink
    biomechanics: '#06B6D4', // Cyan
    fatigue: '#F59E0B',     // Amber
    psychology: '#6366F1',  // Indigo
    officiating: '#78716C', // Stone
    dagger: '#DC2626',      // Red-600
  };
  return colors[category] || '#A3A3A3';
}

export default BasketballVideoPlayer;
